<template>
    <div id="mellon-webecr-settings"
         data-component="MellonWebECRSettingsTab"
         class="modl-tab-body">
        <div class="modl-body">
            <form class="form-h"
                  @submit.prevent="submit">
                <ErrorMessage v-if="error"
                              :error="error" />

                <PageLoadingSpinner v-if="loading" />
                <template v-else>
                    <div v-if="accessTokenDetails !== null"
                         class="form-group">
                        <label for="mellon-webecr-access-token"
                               class="control-label">
                            {{ l('MELLON_WEBECR.ACCESS_TOKEN') }}
                        </label>
                        <div class="form-h__right">
                            <div class="flex flex--align-center">
                                <span>
                                    {{ l('MELLON_WEBECR.ACCESS_TOKEN__EXPIRES') }}
                                    {{ timeFilter(accessTokenDetails.expiresAt, true, true) }}
                                </span>
                                <SquareBtn color="primary"
                                           class="ml-05"
                                           :flex="true"
                                           :loading="refreshingAccessToken"
                                           @click="refreshAccessToken()">
                                    {{ l('MELLON_WEBECR.ACCESS_TOKEN__REFRESH') }}
                                </SquareBtn>
                            </div>
                        </div>
                    </div>

                    <hr />

                    <!-- This button is really for managing API keys,
                    but it's more user friendly to call these terminals. -->
                    <div class="form-group">
                        <label class="control-label">
                            {{ l('MELLON_WEBECR.TERMINALS') }}
                        </label>
                        <div class="form-h__right">
                            <div class="flex flex--align-center">
                                <SquareBtn size="lg"
                                           color="info"
                                           @click="manageApiKeys()">
                                    <i class="fa-solid fa-tablet"></i>
                                    {{ l('MELLON_WEBECR.MANAGE_TERMINALS_BTN') }}
                                </SquareBtn>
                            </div>
                        </div>
                    </div>

                    <div v-if="terminals !== null"
                         class="form-group">
                        <label for="mellon-webecr-terminal"
                               class="control-label">
                            {{ l('MELLON_WEBECR.SELECTED_TERMINAL') }}
                        </label>
                        <select id="mellon-webecr-terminal"
                                v-model="selectedTerminal"
                                class="form-control input-lg">
                            <option v-for="terminal in terminals"
                                    :key="terminal.Id"
                                    :value="terminal">
                                {{ terminal.FriendlyName }} ({{ terminal.TerminalID }})
                            </option>
                        </select>
                    </div>

                    <hr />

                    <div class="form-group">
                        <label for="mellon-webecr-access-token"
                               class="control-label">
                            {{ l('MELLON_WEBECR.RECENT_PAYMENTS_LABEL') }}
                        </label>
                        <div class="form-h__right">
                            <div class="flex flex--align-center">
                                <SquareBtn color="info"
                                           size="lg"
                                           :flex="true"
                                           @click="openTransactionsModal()">
                                    <i class="fa-solid fa-search"></i>
                                    {{ l('MELLON_WEBECR.RECENT_PAYMENTS_BTN') }}
                                </SquareBtn>
                            </div>
                        </div>
                    </div>
                </template>
            </form>
        </div>

        <div class="modl-footer">
            <!-- Cancel button -->
            <button class="btn btn-lg btn-default cancel-btn"
                    @click.prevent="$emit('cancel')">
                {{ l('CANCEL') }}
            </button>

            <!-- OK button -->
            <button class="btn btn-lg btn-primary"
                    :disabled="saving || !okEnabled"
                    @click.prevent="submit">
                <i v-if="saving"
                   class="fa-regular fa-spinner fa-spin" />
                <template v-else>
                    {{ l('SAVE') }}
                </template>
            </button>
        </div>
    </div>
</template>

<script>
import ErrorMessage from '@/vue/components/Errors/ErrorMessage';
import MellonWebECRClient from '@/lib/PaymentMethods/MellonWebECR/MellonWebECRClient';
import PageLoadingSpinner from '@/vue/components/PageLoadingSpinner';
import PaymentMethodRef from '@/lib/PaymentMethods/PaymentMethodRef';
import SquareBtn from '@/vue/components/SquareBtn';
import { container } from '@/container';
import { MODAL_NAMES__EPOS } from '@/epos/vue/modals/MODAL_NAMES__EPOS';
import { modifyDevicePaymentTerminalConfig } from '@/lib/Devices';
import { suppressModalCancelledErrorOrReThrow } from '@/lib/Errors';

export default {
    components: {
        SquareBtn,
        PageLoadingSpinner,
        ErrorMessage,
    },

    emits: [
        'cancel',
        'resolve',
    ],

    data() {
        return {
            /**
             * @type {boolean}
             */
            loading: false,

            /**
             * @type {MellonWebECRTerminal[]}
             */
            terminals: null,

            /**
             * @type {boolean}
             */
            saving: false,

            /**
             * @type {Error}
             */
            error: null,

            /**
             * @type {MellonWebECRDeviceConfig}
             */
            deviceMellonWebECRConfig: {
                apiKey: null,
                terminalID: null,
                terminalTerminalID: null,
            },

            /**
             * @type {string}
             */
            paymentMethodRef: PaymentMethodRef.CARD_MELLON_WEBECR,

            /**
             * The currently selected terminal.
             * This is populated when the component is created, as the terminal that was loaded
             * that matches the terminalID from the config.
             * This is the v-model updated when the user selects a different terminal from the dropdown.
             *
             * @type {?MellonWebECRTerminal}
             */
            selectedTerminal: null,

            /**
             * @type {MellonWebECRAccessTokenDetails|null}
             */
            accessTokenDetails: null,

            /**
             * @type {boolean}
             */
            refreshingAccessToken: false,
        };
    },

    computed: {
        okEnabled() {
            return !!(
                this.deviceMellonWebECRConfig.apiKey
                && this.deviceMellonWebECRConfig.terminalID
                && this.deviceMellonWebECRConfig.terminalTerminalID
            );
        },
    },

    watch: {
        /**
         * Watch for changes to the selected terminal and update the config accordingly.
         *
         * @param {MellonWebECRTerminal} newValue
         */
        selectedTerminal(newValue) {
            if (newValue) {
                this.deviceMellonWebECRConfig.terminalID = newValue.Id;
                this.deviceMellonWebECRConfig.terminalTerminalID = newValue.TerminalID;
            } else {
                this.deviceMellonWebECRConfig.terminalID = null;
                this.deviceMellonWebECRConfig.terminalTerminalID = null;
            }
        },
    },

    async created() {
        /**
         * @type {!Device}
         */
        const device = container.currentDevice.device;

        // Copy any existing values from the device's paymentTerminalConfig.card-mellon-webecr object
        // to the new config (this.deviceMellonWebECRConfig).
        if (device.paymentTerminalConfig?.[this.paymentMethodRef]) {
            Object
                .entries(device.paymentTerminalConfig[this.paymentMethodRef])
                .forEach(([key, value]) => {
                    if (value || value === false) {
                        this.deviceMellonWebECRConfig[key] = value;
                    }
                });
        }

        this.loading = true;
        this.accessTokenDetails = await container.mellonWebECRDriver.checkAccessToken();
        this.loading = false;

        this.loadTerminals();
    },

    methods: {
        /**
         * Load the list of terminals from the Mellon API.
         */
        loadTerminals() {
            this.error = null;

            if (!this.deviceMellonWebECRConfig.apiKey) {
                // Clear the terminals if there is no API key.
                this.terminals = [];
                this.selectedTerminal = null;
                return;
            }

            this.loading = true;

            container.mellonWebECRDriver.client
                .getTerminals(
                    {},
                    {
                        // Specifically use the API key from the work-in-progress device config,
                        // because it might have just changed and not been applied yet if we just
                        // switched to a different API key. We need to show the terminal list for
                        // this new API key not for the old one.
                        [MellonWebECRClient.API_KEY_HEADER]: this.deviceMellonWebECRConfig.apiKey || '',
                    }
                )
                .then((response) => {
                    this.terminals = response.results.filter((t) => t.IsEnabled);

                    // Find the already selected terminal in the results.
                    const selectedTerminal = this.terminals.find(
                        (terminal) => terminal.TerminalID === this.deviceMellonWebECRConfig.terminalTerminalID,
                    );
                    if (selectedTerminal) {
                        // Select the terminal that matches the terminalID from the config.
                        this.selectedTerminal = selectedTerminal;
                    } else if (this.terminals?.length) {
                        // Select the first terminal by default.
                        this.selectedTerminal = this.terminals[0];
                    } else {
                        this.selectedTerminal = null;
                    }
                })
                .catch((error) => {
                    this.error = error;
                })
                .finally(() => {
                    this.loading = false;
                });
        },

        async submit() {
            if (this.saving) {
                return;
            }

            this.saving = true;
            this.error = null;

            try {
                const device = container.currentDevice.device;

                // Build the new device payment terminal config.
                // This is the complete object including values for other payment methods.
                const newDevicePaymentTerminalConfig = modifyDevicePaymentTerminalConfig(
                    device,
                    this.paymentMethodRef,
                    this.deviceMellonWebECRConfig,
                );

                // Send the new config to the API.
                await container.deviceManager.updateDevice(device.ID, {
                    paymentTerminalConfig: newDevicePaymentTerminalConfig,
                });

                // Update the config on the device.
                container.currentDevice.device.paymentTerminalConfig = newDevicePaymentTerminalConfig;
                container.currentDevice.cache();

                container.toastFactory.success(
                    'MELLON_WEBECR.SETTINGS_SAVED',
                );

                // Update the config in use by the driver and client.
                await container.mellonWebECRDriver.loadConfig();

                this.$emit('resolve');
            } catch (e) {
                this.error = e;
            } finally {
                this.saving = false;
            }
        },

        refreshAccessToken() {
            if (this.refreshingAccessToken) {
                return;
            }

            this.refreshingAccessToken = true;

            container.mellonWebECRDriver
                .checkAccessToken(true)
                .then((result) => {
                    this.accessTokenDetails = result;
                })
                .catch((error) => {
                    this.error = error;
                })
                .finally(() => {
                    this.refreshingAccessToken = false;
                });
        },

        manageApiKeys() {
            container.vueModalFactory
                .open(
                    MODAL_NAMES__EPOS.MELLON_WEBECR_API_KEYS,
                    {
                        selectedApiKey: this.deviceMellonWebECRConfig.apiKey,
                    }
                )
                .then(({ apiKeyData }) => {
                    console.log('Using new API key', apiKeyData);

                    // Update the device API key in use.
                    // Note that this won't be saved until the user the clicks save button.
                    this.deviceMellonWebECRConfig.apiKey = apiKeyData.apiKey;
                })
                .catch(suppressModalCancelledErrorOrReThrow)
                .finally(() => {
                    // After closing the API keys modal, reload the terminals.
                    this.loadTerminals();
                });
        },

        openTransactionsModal() {
            container.vueModalFactory.openAndIgnoreResult(
                MODAL_NAMES__EPOS.MELLON_WEBECR_TRANSACTIONS,
            );
        },
    },
};
</script>
